import booleanIntersects from "@turf/boolean-intersects";
import { MultiLineString } from "geojson";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getExistingLeveeEdits } from "../../../state/existingEvents";
import { getModelGridFlowLayerOpen } from "../../../state/layerCalculationMenu";
import { getPoints } from "../../../state/leveeEdit";
import { getMapBounds, getMouseLocation } from "../../../state/map";
import { GridCellFlowLineCollection, getGridFlowLines } from "../../../state/simulation";
import { filterLineStringGeoJsonWithinBounds } from "../../../utils/polygonUtils";

const useFlowLinesLeveeIntersecting = (): GridCellFlowLineCollection["features"] => {
  const newMapBounds = useSelector(getMapBounds);
  const flowLineLayerVisible = useSelector(getModelGridFlowLayerOpen);

  const originalGridFlowLines = useSelector(getGridFlowLines);

  const existingLeveeEdits = useSelector(getExistingLeveeEdits);
  const points = useSelector(getPoints);
  const mouseLocation = useSelector(getMouseLocation);

  const [pathsInMathBounds, setPathsInMapBounds] = useState<GridCellFlowLineCollection["features"]>(
    []
  );
  const [intersectingPaths, setIntersectingPaths] = useState<
    GridCellFlowLineCollection["features"]
  >([]);

  useEffect(() => {
    if (flowLineLayerVisible && newMapBounds)
      setPathsInMapBounds(
        filterLineStringGeoJsonWithinBounds(newMapBounds, originalGridFlowLines)
          .features as GridCellFlowLineCollection["features"]
      );
  }, [flowLineLayerVisible, newMapBounds, originalGridFlowLines]);

  useEffect(() => {
    if (flowLineLayerVisible) {
      const leveePaths: MultiLineString = {
        type: "MultiLineString",
        coordinates: existingLeveeEdits.map((edit) => edit.linestring.coordinates),
      };
      if (points) {
        const pushCoordinates = points.map((point) => point);
        if (mouseLocation) pushCoordinates.push(mouseLocation);
        leveePaths.coordinates.push(pushCoordinates);
      }

      const newIntersectingPaths: GridCellFlowLineCollection["features"] = [];

      pathsInMathBounds.forEach((flowLine) => {
        booleanIntersects(leveePaths, flowLine.geometry) && newIntersectingPaths.push(flowLine);
      });

      if (
        intersectingPaths.length !== newIntersectingPaths.length ||
        intersectingPaths.find(
          (path, index) =>
            newIntersectingPaths[index] &&
            path.properties.id !== newIntersectingPaths[index].properties.id
        )
      )
        setIntersectingPaths(newIntersectingPaths);
    }
  }, [
    existingLeveeEdits,
    flowLineLayerVisible,
    intersectingPaths,
    mouseLocation,
    pathsInMathBounds,
    points,
  ]);

  return intersectingPaths;
};

export default useFlowLinesLeveeIntersecting;
